import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const HarrisonHotSprings = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.harrison-hot-springs:About Harrison Hot Springs Paragraph 1">
          Harrison Hot Springs, a serene resort village nestled in the Fraser
          Valley of{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Canada, is a haven of natural beauty and relaxation. Its fame stems
          from the soothing natural hot springs, which have drawn visitors for
          generations. Surrounded by the majestic Coast Mountains and Harrison
          Lake, the village offers a picturesque backdrop for outdoor
          enthusiasts, with activities like hiking, boating, and wildlife
          watching. The annual Harrison Festival of the Arts showcases its
          vibrant cultural scene, complementing the artistic heritage. Sandy
          beaches, a variety of dining options, and a range of accommodations,
          including the iconic Harrison Hot Springs Resort & Spa, cater to
          visitors. This charming village is a hidden gem among the many
          enchanting{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>
          , offering a tranquil escape from the everyday hustle and bustle.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,000 - $2,000',
          bed2: '$1,200 - $2,500',
          bed3: '$2,000 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.harrison-hot-springs:Apartment Hunting in Harrison Hot Springs Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Harrison Hot Springs
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.harrison-hot-springs:Apartments for rent Harrison Hot Springs Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Harrison Hot Springs
              </LinkText>{' '}
              typically attract those who appreciate a compact, easy-to-maintain
              living space, often with the bonus of scenic views. These units,
              from quaint older styles to contemporary designs, are perfect for
              individuals or small families who want to be close to the
              village’s amenities and the famous hot springs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.harrison-hot-springs:Houses for rent Harrison Hot Springs Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Harrison Hot Springs
              </LinkText>{' '}
              provide a more traditional and spacious living experience. These
              homes, which can range from rustic cabins to modern family homes,
              often come with gardens or yards, allowing residents to immerse
              themselves in the serene, natural beauty of the area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.harrison-hot-springs:Townhouses for rent Harrison Hot Springs Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Harrison Hot Springs
              </LinkText>{' '}
              offer a blend of privacy and community, typically featuring
              multi-level living spaces. These units are popular among those who
              seek a balance between the convenience of a managed property and
              the feel of a standalone home, often appealing to small families
              or couples.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.harrison-hot-springs:Basements for rent Harrison Hot Springs Paragraph">
              Basements for rent in Harrison Hot Springs provide an affordable
              and cozy option. These lower-level suites, usually part of a
              larger home, offer a secluded living space. They’re ideal for
              those who prioritize budget-friendly living and don’t mind the
              trade-off in natural lighting.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.harrison-hot-springs:Condos for rent Harrison Hot Springs Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Harrison Hot Springs
              </LinkText>{' '}
              are sought after for their blend of luxury and convenience. Many
              condos offer amenities like pools or fitness centers, and they’re
              particularly appealing to those who seek a carefree lifestyle with
              easy access to the village’s recreational activities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.harrison-hot-springs:Rooms for rent Harrison Hot Springs Paragraph">
              Renting a room is an economical and flexible option, typically
              chosen by students, seasonal workers, or solo travelers.{' '}
              <LinkText href={links.room}>
                Rooms for rent in Harrison Hot Springs
              </LinkText>{' '}
              typically involve shared common areas, which is perfect for those
              looking to enjoy the social aspects of communal living and the
              opportunity to meet new people in a welcoming community.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.harrison-hot-springs:Short term rentals Harrison Hot Springs Paragraph">
              Harrison Hot Springs, primarily known as a resort village in
              British Columbia, offers employment opportunities mostly in the
              hospitality and tourism sectors, owing to its famous hot springs
              and picturesque lake. It is not particularly noted for its
              academic institutions, as it is a smaller community focused more
              on tourism. The demand for{' '}
              <LinkText href={links.shortTerm}>
                short-term rentals in Harrison Hot Springs
              </LinkText>{' '}
              is significant, especially during the peak tourist seasons, like
              the summer months. This pattern differs from larger cities where
              short-term rentals are popular year-round and particularly at the
              beginning of school semesters.
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                The flexibility of short-term rentals is appealing to many
              </LinkText>
              , especially those involved in seasonal work or those seeking a
              temporary retreat in this scenic area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.harrison-hot-springs:Cheap rentals Harrison Hot Springs Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Harrison Hot Springs
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Harrison Hot Springs, there are
              neighbourhoods where rent is considerably cheaper, so exploring
              these options is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.harrison-hot-springs:Living in Harrison Hot Springs Paragraph 1">
            Nestled in the heart of British Columbia, Harrison Hot Springs
            offers a charming blend of natural beauty and small-town allure.
            Situated in the Fraser Valley region, this idyllic village is
            surrounded by picturesque mountains, lush forests, and crystal-clear
            waters. Renowned for its warm and friendly atmosphere, Harrison Hot
            Springs provides a plethora of amenities for both residents and
            tourists. Outdoor enthusiasts can engage in activities like hiking,
            fishing, and boating, while culture enthusiasts can explore the
            local arts and historical landmarks. With its serene ambiance and
            relaxed lifestyle, Harrison Hot Springs is the ultimate destination
            for a tranquil coastal retreat.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.harrison-hot-springs:Living in Harrison Hot Springs Paragraph 2">
            If you’re not sure that Harrison hot Springs is the right city for
            you, check out some of these resources comparing the different
            cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.harrison-hot-springs:Popular Neighbourhoods Harrison Hot Springs Paragraph">
          Like any city, Harrison Hot Springs is made up of smaller
          neighbourhoods with their own distinct features. Some of the most
          popular neighbourhoods include Village Center, Lagoon Drive, Harrison
          Lake, Springs Drive, and Esplanade Avenue. For more info about
          Harrison Hot Springs, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.harrison-hot-springs:Local Landmarks Harrison Hot Springs Paragraph">
          Harrison Hot Springs, BC, is a destination known for its remarkable
          landmarks that showcase the town’s natural beauty and cultural
          significance. One of the notable landmarks in Harrison Hot Springs is
          the Harrison Hot Springs Resort and Spa, a renowned hotel and spa
          offering relaxation and rejuvenation amidst stunning mountain views
          and hot springs. Another prominent landmark is the Harrison
          Waterfront, a picturesque area where visitors can enjoy lakeside
          walks, recreational activities, and breathtaking sunsets. For history
          enthusiasts, the Agassiz-Harrison Museum offers a glimpse into the
          region’s past, with exhibits that highlight its pioneer heritage and
          local culture. Lastly, the iconic Spirit Trail is a must-visit
          landmark, providing a serene and scenic hiking experience through
          old-growth forests and along the Harrison River. These landmarks make
          Harrison Hot Springs a captivating destination that appeals to both
          nature lovers and history buffs, offering a unique and unforgettable
          experience.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default HarrisonHotSprings
